import React, { useState, useEffect } from "react";
import {
  Dialog,
  Typography,
  Box,
  TextField,
  CircularProgress,
} from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";
import {
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
  CancelButton,
  ConfirmButton,
} from "../../shared/components";

interface SetCountModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (newCount: number) => void;
  heatNumber: string;
  position: number;
  currentCount: number;
  isLoading?: boolean;
}

export const SetCountModal = ({
  open,
  onClose,
  onConfirm,
  heatNumber,
  position,
  currentCount,
  isLoading = false,
}: SetCountModalProps) => {
  const [newCount, setNewCount] = useState<string>("");
  const [error, setError] = useState<string>("");

  // Initialize the input with current count when modal opens
  useEffect(() => {
    if (open) {
      setNewCount(currentCount.toString());
      setError("");
    }
  }, [open, currentCount]);

  const handleCountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setNewCount(value);
    setError("");
  };

  const handleConfirm = () => {
    const count = parseInt(newCount);

    if (isNaN(count) || count < 0) {
      setError("Please enter a valid positive number.");
      return;
    }

    if (count === currentCount) {
      setError("New count must be different from current count.");
      return;
    }

    onConfirm(count);
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !isLoading && !error) {
      handleConfirm();
    }
  };

  const isValidInput = () => {
    const count = parseInt(newCount);
    return !isNaN(count) && count >= 0 && count !== currentCount;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown={isLoading}
      sx={{
        "& .MuiDialog-paper": {
          maxWidth: "500px",
          width: "90vw",
          borderRadius: "12px",
        },
      }}
    >
      <StyledDialogTitle colorScheme="primary">
        <Typography variant="h5" component="div">
          <EditIcon fontSize="large" />
          Set Billet Count
        </Typography>
      </StyledDialogTitle>

      <StyledDialogContent>
        <Typography
          sx={{
            fontSize: "18px",
            fontWeight: "bold",
            color: (theme) => theme.palette.primary.main,
            marginBottom: (theme) => theme.spacing(2),
          }}
        >
          {heatNumber} - Position {position + 1}
        </Typography>

        <Box
          sx={{
            backgroundColor: (theme) =>
              theme.palette.billetYard.backgrounds.light,
            padding: (theme) => theme.spacing(2),
            borderRadius: "8px",
            marginBottom: (theme) => theme.spacing(3),
            border: (theme) =>
              `1px solid ${theme.palette.billetYard.borders.medium}`,
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              color: (theme) => theme.palette.text.secondary,
              marginBottom: (theme) => theme.spacing(1),
            }}
          >
            Current Count
          </Typography>
          <Typography
            sx={{
              fontSize: "24px",
              fontWeight: "bold",
              color: (theme) => theme.palette.primary.main,
            }}
          >
            {currentCount} billets
          </Typography>
        </Box>

        <TextField
          fullWidth
          variant="outlined"
          label="New Count"
          type="number"
          value={newCount}
          onChange={handleCountChange}
          onKeyPress={handleKeyPress}
          disabled={isLoading}
          autoFocus
          slotProps={{
            htmlInput: {
              min: 0,
              step: 1,
            },
          }}
          sx={{
            marginBottom: (theme) => theme.spacing(2),
            "& .MuiOutlinedInput-root": {
              fontSize: "18px",
              "& fieldset": {
                borderColor: (theme) => theme.palette.billetYard.borders.dark,
              },
              "&:hover fieldset": {
                borderColor: (theme) => theme.palette.primary.main,
              },
              "&.Mui-focused fieldset": {
                borderColor: (theme) => theme.palette.primary.main,
              },
            },
            "& .MuiInputLabel-root": {
              fontSize: "16px",
              "&.Mui-focused": {
                color: (theme) => theme.palette.primary.main,
              },
            },
          }}
        />

        {error && (
          <Typography
            sx={{
              color: (theme) => theme.palette.error.main,
              fontSize: "14px",
              marginTop: (theme) => theme.spacing(1),
            }}
          >
            {error}
          </Typography>
        )}

        <Typography
          sx={{
            fontSize: "16px",
            lineHeight: 1.5,
            color: (theme) => theme.palette.text.primary,
            marginBottom: (theme) => theme.spacing(3),
          }}
        >
          Enter the new count for this billet position.
        </Typography>
      </StyledDialogContent>

      <StyledDialogActions>
        <CancelButton onClick={handleClose} disabled={isLoading}>
          Cancel
        </CancelButton>

        <ConfirmButton
          onClick={handleConfirm}
          disabled={isLoading || !isValidInput()}
        >
          {isLoading && (
            <CircularProgress
              size={20}
              sx={{ marginRight: "8px", color: "white" }}
            />
          )}
          Set Count
        </ConfirmButton>
      </StyledDialogActions>
    </Dialog>
  );
};
